import { Form } from "@ant-design/react-native";
import { IInputProps, Input } from "@shared/components/md-design-system/Input";
import { PhoneInput } from "@shared/components/md-design-system/PhoneInput";
import { useState, useCallback, useEffect } from "react";

export interface IFormInput extends IInputProps {
    name: string;
    rules?: any[];
    type?: 'text' | 'phone';
    prefixName?: string;
    prefixRules?: any[];
    unit?: string;
}

export interface IFormConfig {
    inputs: IFormInput[];
    initialValues?: Record<string, any>;
    onSubmit?: (values: any) => void | Promise<void>;
    onValidationError?: (errors: any[]) => void;
    translate?: (key: string) => string;
}

export const useForm = (config?: IFormConfig) => {
    const [form] = Form.useForm();
    const [formErrors, setFormErrors] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formVersion, setFormVersion] = useState(0);

    useEffect(() => {
        if (config?.initialValues) {
            form.setFieldsValue(config.initialValues);
            setFormVersion(v => v + 1);
        }
    }, [config?.initialValues, form]);

    const handleInputFocus = useCallback((name: string, prefixName?: string) => {
        let updatedErrors = formErrors.filter(error => error.name[0] !== name);
        if (prefixName) {
            updatedErrors = updatedErrors.filter(error => error.name[0] !== prefixName);
        }
        setFormErrors(updatedErrors);
    }, [formErrors]);

    const handleInputChange = useCallback((name: string, text: string, prefixName?: string) => {
        form.setFieldValue(name, text);
        let updatedErrors = formErrors.filter(error => error.name[0] !== name);
        if (prefixName) {
            updatedErrors = updatedErrors.filter(error => error.name[0] !== prefixName);
        }
        setFormErrors(updatedErrors);

        setTimeout(() => {
            form.validateFields([name]).catch(() => { });
        }, 300);
    }, [form, formErrors]);

    const handleInputError = useCallback((input: IFormInput) => {
        const fieldError = formErrors.find(error => error.name[0] === input.name);
        const prefixError = input.prefixName ? formErrors.find(error => error.name[0] === input.prefixName) : null;

        const hasMainError = fieldError?.errors.length > 0;
        const hasPrefixError = prefixError?.errors.length > 0;
        const hasError = hasMainError || hasPrefixError;

        const rawErrorMessage = hasMainError ? fieldError.errors[0] : (hasPrefixError ? prefixError.errors[0] : '');
        const errorMessage = config?.translate && rawErrorMessage ? config.translate(rawErrorMessage) : rawErrorMessage;

        return { hasError, errorMessage };
    }, [formErrors, config?.translate]);

    const handleSubmit = useCallback(async () => {
        if (!config?.onSubmit) return;
        setIsSubmitting(true);

        try {
            await form.validateFields();
            const values = form.getFieldsValue();
            setFormErrors([]);
            await config.onSubmit(values);
        } catch (errorInfo) {
            const errors = form.getFieldsError();
            setFormErrors(errors);
            config.onValidationError?.(errors);
        } finally {
            setIsSubmitting(false);
        }
    }, [form, config]);

    const renderFormField = useCallback((input: IFormInput) => {
        const { hasError, errorMessage } = handleInputError(input);

        if (input.type === 'phone' && input.prefixName) {
            return {
                ...input,
                hasError,
                errorMessage,
                phoneValue: form.getFieldValue(input.name) || '',
                prefixValue: form.getFieldValue(input.prefixName) || '+39',
                onFocus: () => handleInputFocus(input.name, input.prefixName),
                onPhoneChange: (text: string) => handleInputChange(input.name, text, input.prefixName),
                onPrefixChange: (prefix: string) => handleInputChange(input.prefixName!, prefix, input.name),
            };
        }

        return {
            ...input,
            hasError,
            errorMessage,
            value: form.getFieldValue(input.name) || '',
            onFocus: () => handleInputFocus(input.name),
            onChangeText: (text: string) => handleInputChange(input.name, text),
        };
    }, [form, formVersion, handleInputError, handleInputFocus, handleInputChange]);

    const createFormFields = useCallback((inputs: IFormInput[]) => {
        return inputs.map(input => ({
            ...renderFormField(input),
            ...(input.rules ? { rules: input.rules } : {})
        }));
    }, [renderFormField]);

    return {
        form,
        formErrors,
        setFormErrors,
        isSubmitting,

        Form,
        Input,
        PhoneInput,

        handleInputFocus,
        handleInputChange,
        handleInputError,
        handleSubmit,

        renderFormField,
        createFormFields,

        formFields: config?.inputs ? createFormFields(config.inputs) : [],
        submitForm: handleSubmit
    }
}